import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Loader2, Bell } from 'lucide-react';
import { NotificationPreferences } from '../../lib/api';

interface NotificationsTabProps {
  notifPrefs: NotificationPreferences | null;
  loadingNotifPrefs: boolean;
  savingNotifPrefs: boolean;
  onPrefChange: (key: keyof NotificationPreferences, value: boolean) => void;
}

export function NotificationsTab({
  notifPrefs,
  loadingNotifPrefs,
  savingNotifPrefs,
  onPrefChange,
}: NotificationsTabProps) {
  if (loadingNotifPrefs) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading notification preferences...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Sales Order Notifications
          </CardTitle>
          <CardDescription>
            Get notified about sales order updates and delivery status.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>In-App Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive notifications within the application</p>
            </div>
            <Switch
              checked={notifPrefs?.po_updates_in_app ?? true}
              onCheckedChange={(checked) => onPrefChange('po_updates_in_app', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive email when PO status changes</p>
            </div>
            <Switch
              checked={notifPrefs?.po_updates_email ?? true}
              onCheckedChange={(checked) => onPrefChange('po_updates_email', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Delivery Notifications</CardTitle>
          <CardDescription>
            Stay informed about upcoming deliveries and delays.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>In-App Notifications</Label>
              <p className="text-sm text-muted-foreground">Get notified about delivery updates</p>
            </div>
            <Switch
              checked={notifPrefs?.delivery_notifications_in_app ?? true}
              onCheckedChange={(checked) => onPrefChange('delivery_notifications_in_app', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive email for delivery alerts</p>
            </div>
            <Switch
              checked={notifPrefs?.delivery_notifications_email ?? true}
              onCheckedChange={(checked) => onPrefChange('delivery_notifications_email', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Other Notifications</CardTitle>
          <CardDescription>
            System announcements.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>System Announcements</Label>
              <p className="text-sm text-muted-foreground">Important system updates</p>
            </div>
            <Switch
              checked={notifPrefs?.system_announcements_in_app ?? true}
              onCheckedChange={(checked) => onPrefChange('system_announcements_in_app', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
